/**
 * Point_Discount at the counter (Req 18, 28.2). Online only (Req 28.4).
 *
 *  1. Purchase amount, points and the member's confirmation: an SMS code requested for
 *     exactly these points (`requestDiscountOtp`), or a fresh Member QR (Req 18.6).
 *  2. `createPointDiscount` reserves the points as a 15-minute Hold and returns the LKR
 *     discount and the member's tier discount (Req 18.1, 18.5); a countdown shows the
 *     time left.
 *  3. Confirm with the invoice number (commits, Req 18.3) or cancel (voids, Req 18.4).
 *     Once the Hold has expired the confirm action is withdrawn.
 *
 * Every ledger-affecting call carries an Idempotency-Key reused for retries of the same
 * payload, so a lost response never reserves or commits twice.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { formatLkr, formatPercentBps, formatPoints } from '@macksons/i18n';
import { asApiError, callOperation } from '@macksons/web-client';
import { useLocale, useT } from '../hooks';
import { classifyScan, isInvoiceNo, isMemberQr, isOtp, lkrToMinor, toPositivePoints } from '../scanner/inputs';
import { scanMismatchText } from '../scanner/messages';
import { useServices } from '../services';
import type { MemberLookupResult, OtpRequested, PointDiscount } from '../types';
import { QrScanner } from './QrScanner';
import {
  FeedbackRegion,
  formatCountdown,
  OnlineOnlyNotice,
  ResultCard,
  useErrorText,
  useIdempotencyKey,
  useNow,
  useOnline,
  type Feedback,
} from './ui';

type Method = 'otp' | 'qr';
type Outcome = 'committed' | 'cancelled' | 'expired';
type Phase = { kind: 'form' } | { kind: 'hold'; hold: PointDiscount } | { kind: 'done'; hold: PointDiscount; outcome: Outcome };

function HoldView({
  hold,
  online,
  onDone,
  setFeedback,
}: {
  hold: PointDiscount;
  online: boolean;
  onDone: (hold: PointDiscount, outcome: Outcome) => void;
  setFeedback: (f: Feedback) => void;
}) {
  const t = useT();
  const locale = useLocale();
  const errorText = useErrorText();
  const { client } = useServices();
  const id = useId();
  const now = useNow(1000);
  const confirmKey = useIdempotencyKey();
  const cancelKey = useIdempotencyKey();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const invoiceRef = useRef<HTMLInputElement>(null);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceError, setInvoiceError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const remaining = Date.parse(hold.expiresAt) - now;
  const expired = remaining <= 0;
  useEffect(() => headingRef.current?.focus(), []);
  useEffect(() => {
    if (expired) setFeedback({ tone: 'error', text: t('operator.discount.expired') });
  }, [expired, setFeedback, t]);
  const finish = (res: PointDiscount, wanted: 'committed' | 'voided') => {
    if (res.status === 'committed') onDone(res, 'committed');
    else if (res.status === 'voided') onDone(res, wanted === 'voided' ? 'cancelled' : 'expired');
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    const inv = invoiceNo.trim();
    if (!isInvoiceNo(inv)) {
      setInvoiceError(t('operator.invoice.invalidInvoiceNo'));
      invoiceRef.current?.focus();
      return;
    }
    setInvoiceError(undefined);
    setBusy(true);
    try {
      const body = { invoiceNo: inv };
      const res = await callOperation<PointDiscount>(client, 'confirmPointDiscount', {
        pathParams: { holdId: hold.holdId },
        body,
        headers: { 'Idempotency-Key': confirmKey.keyFor(`${hold.holdId}|${inv}`) },
      });
      finish(res, 'committed');
    } catch (cause) {
      const err = asApiError(cause);
      if (!err.retryable) confirmKey.reset();
      setFeedback({ tone: 'error', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    setBusy(true);
    try {
      const res = await callOperation<PointDiscount>(client, 'cancelPointDiscount', {
        pathParams: { holdId: hold.holdId },
        body: {},
        headers: { 'Idempotency-Key': cancelKey.keyFor(hold.holdId) },
      });
      finish(res, 'voided');
    } catch (cause) {
      const err = asApiError(cause);
      if (!err.retryable) cancelKey.reset();
      setFeedback({ tone: 'error', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hold" data-testid="hold">
      <h4 ref={headingRef} tabIndex={-1}>
        {t('operator.discount.hold.title')}
      </h4>
      <dl className="facts">
        <div>
          <dt>{t('operator.discount.hold.discount')}</dt>
          <dd data-testid="hold-discount">{formatLkr(BigInt(hold.discountMinor), locale)}</dd>
        </div>
        <div>
          <dt>{t('operator.discount.hold.points')}</dt>
          <dd>{formatPoints(BigInt(hold.points), locale)}</dd>
        </div>
        <div>
          <dt>{t('operator.discount.hold.tierDiscount')}</dt>
          <dd data-testid="hold-tier">{formatPercentBps(hold.tierDiscountBps, locale)}</dd>
        </div>
      </dl>
      {/* Visual countdown only: announcing every second would drown out everything else;
          expiry itself is announced through the alert region. */}
      <p className="countdown" data-testid="countdown">
        {expired ? t('operator.discount.hold.expiredShort') : t('operator.discount.hold.expiresIn', { time: formatCountdown(remaining) })}
      </p>
      <form onSubmit={(e) => void confirm(e)} noValidate className="form">
        <div className="field">
          <label htmlFor={`${id}-inv`}>{t('invoice.invoiceNo')}</label>
          <input
            ref={invoiceRef}
            id={`${id}-inv`}
            className="input"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            aria-invalid={invoiceError !== undefined}
            aria-describedby={invoiceError ? `${id}-inv-err` : undefined}
            autoComplete="off"
            maxLength={64}
            disabled={expired || !online}
          />
          {invoiceError && (
            <p id={`${id}-inv-err`} className="field__error">
              {invoiceError}
            </p>
          )}
        </div>
        <div className="actions">
          <button type="submit" className="button button--primary" disabled={expired || busy || !online}>
            {t('operator.discount.confirm')}
          </button>
          <button type="button" className="button" onClick={() => void cancel()} disabled={busy || !online}>
            {t('operator.discount.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

export function DiscountPanel({ member, outletId, active }: { member: MemberLookupResult; outletId: string; active: boolean }) {
  const t = useT();
  const locale = useLocale();
  const errorText = useErrorText();
  const { client, config } = useServices();
  const online = useOnline();
  const id = useId();
  const createKey = useIdempotencyKey();
  const amountRef = useRef<HTMLInputElement>(null);
  const pointsRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const newRef = useRef<HTMLButtonElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  const [amount, setAmount] = useState('');
  const [points, setPoints] = useState('');
  const [method, setMethod] = useState<Method>('otp');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState<{ points: string; minutes: number } | undefined>();
  const [qrToken, setQrToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>();
  const pointsValue = toPositivePoints(points);
  // The SMS code is bound to the requested points: changing them needs a new code.
  const otpValid = otpSent !== undefined && otpSent.points === pointsValue;
  useEffect(() => {
    if (phase.kind === 'done') newRef.current?.focus();
  }, [phase.kind]);
  useEffect(() => {
    if (otpSent) otpRef.current?.focus();
  }, [otpSent]);
  const validate = (): { purchaseAmountMinor: string; points: string } | undefined => {
    const purchaseAmountMinor = lkrToMinor(amount);
    if (!purchaseAmountMinor) {
      setFeedback({ tone: 'error', text: t('operator.invoice.invalidAmount') });
      amountRef.current?.focus();
      return undefined;
    }
    if (!pointsValue) {
      setFeedback({ tone: 'error', text: t('operator.discount.invalidPoints') });
      pointsRef.current?.focus();
      return undefined;
    }
    if (BigInt(pointsValue) > BigInt(member.available)) {
      setFeedback({ tone: 'error', text: t('errors.insufficient_balance') });
      pointsRef.current?.focus();
      return undefined;
    }
    return { purchaseAmountMinor, points: pointsValue };
  };
  const sendOtp = async () => {
    if (!pointsValue) {
      setFeedback({ tone: 'error', text: t('operator.discount.invalidPoints') });
      pointsRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const res = await callOperation<OtpRequested>(client, 'requestDiscountOtp', {
        body: { memberId: member.memberId, outletId, points: pointsValue },
      });
      const minutes = Math.max(1, Math.round(res.expiresInSeconds / 60));
      setOtpSent({ points: pointsValue, minutes });
      setOtp('');
      setFeedback({ tone: 'ok', text: t('operator.discount.otpSent', { count: minutes }) });
    } catch (cause) {
      setFeedback({ tone: 'error', text: errorText(cause) });
    } finally {
      setBusy(false);
    }
  };
  const reserve = async (e: FormEvent) => {
    e.preventDefault();
    const values = validate();
    if (!values) return;
    let confirmation: { otp: string } | { memberQrToken: string };
    if (method === 'otp') {
      if (!otpValid) {
        setFeedback({ tone: 'error', text: t('operator.discount.otpFirst') });
        return;
      }
      if (!isOtp(otp)) {
        setFeedback({ tone: 'error', text: t('operator.discount.invalidOtp') });
        otpRef.current?.focus();
        return;
      }
      confirmation = { otp: otp.trim() };
    } else {
      if (!isMemberQr(qrToken.trim())) {
        setFeedback({ tone: 'error', text: t('operator.discount.missingQr') });
        return;
      }
      confirmation = { memberQrToken: qrToken.trim() };
    }
    const body = { memberId: member.memberId, outletId, ...values, confirmation };
    setBusy(true);
    try {
      const hold = await callOperation<PointDiscount>(client, 'createPointDiscount', {
        body,
        headers: { 'Idempotency-Key': createKey.keyFor(JSON.stringify(body)) },
      });
      createKey.reset();
      // Both confirmations are single use.
      setOtp('');
      setOtpSent(undefined);
      setQrToken('');
      setFeedback(undefined);
      setPhase({ kind: 'hold', hold });
    } catch (cause) {
      const err = asApiError(cause);
      if (!err.retryable) createKey.reset();
      if (err.code === 'invalid_member_qr') setQrToken('');
      setFeedback({ tone: 'error', text: errorText(err) });
      if (err.code === 'invalid_otp') otpRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };
  const onQrScan = (text: string) => {
    const scan = classifyScan(text, config.qrHost);
    if (scan.kind === 'member_qr') {
      setQrToken(scan.token);
      setFeedback({ tone: 'ok', text: t('operator.discount.qrCaptured') });
    } else setFeedback({ tone: 'error', text: scanMismatchText(t, scan, 'member') });
  };
  const startOver = () => {
    setPhase({ kind: 'form' });
    setAmount('');
    setPoints('');
    setFeedback(undefined);
    setTimeout(() => amountRef.current?.focus(), 0);
  };
  const onDone = (hold: PointDiscount, outcome: Outcome) => {
    setPhase({ kind: 'done', hold, outcome });
    setFeedback({
      tone: outcome === 'committed' ? 'ok' : 'info',
      text:
        outcome === 'committed'
          ? t('operator.discount.committed', { amount: formatLkr(BigInt(hold.discountMinor), locale), invoiceNo: hold.invoiceNo ?? '' })
          : outcome === 'cancelled'
            ? t('operator.discount.cancelled')
            : t('operator.discount.expired'),
    });
  };
  const formDisabled = !online || busy;
  return (
    <div className="form-panel">
      <h3>{t('discount.title')}</h3>
      {!online && <OnlineOnlyNotice />}
      {phase.kind === 'form' && (
        <form onSubmit={(e) => void reserve(e)} noValidate className="form">
          <div className="field">
            <label htmlFor={`${id}-amt`}>{t('discount.purchaseAmount')}</label>
            <input
              ref={amountRef}
              id={`${id}-amt`}
              className="input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              autoComplete="off"
              disabled={formDisabled}
            />
          </div>
          <div className="field">
            <label htmlFor={`${id}-pts`}>{t('discount.pointsToUse')}</label>
            <p id={`${id}-pts-hint`} className="hint">
              {t('operator.discount.available', { points: formatPoints(BigInt(member.available), locale) })}
            </p>
            <input
              ref={pointsRef}
              id={`${id}-pts`}
              className="input"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              inputMode="numeric"
              aria-describedby={`${id}-pts-hint`}
              autoComplete="off"
              disabled={formDisabled}
            />
          </div>
          <fieldset className="fieldset">
            <legend>{t('operator.discount.method')}</legend>
            {(['otp', 'qr'] as const).map((m) => (
              <label key={m} className="radio-card">
                <input type="radio" name={`${id}-method`} value={m} checked={method === m} onChange={() => setMethod(m)} disabled={formDisabled} />
                {t(m === 'otp' ? 'discount.confirmWithOtp' : 'discount.confirmWithQr')}
              </label>
            ))}
          </fieldset>
          {method === 'otp' ? (
            <>
              <button type="button" className="button" onClick={() => void sendOtp()} disabled={formDisabled}>
                {otpValid ? t('operator.discount.resendOtp') : t('operator.discount.sendOtp')}
              </button>
              {otpValid && (
                <div className="field">
                  <label htmlFor={`${id}-otp`}>{t('operator.discount.otpLabel')}</label>
                  <input
                    ref={otpRef}
                    id={`${id}-otp`}
                    className="input input--otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    disabled={formDisabled}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="field">
              <p className="hint">{t('operator.discount.qrHint')}</p>
              <QrScanner onScan={onQrScan} startLabel={t('discount.confirmWithQr')} disabled={formDisabled || !active} />
              <label htmlFor={`${id}-qr`}>{t('operator.discount.qrLabel')}</label>
              <input
                id={`${id}-qr`}
                className="input"
                value={qrToken}
                onChange={(e) => setQrToken(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                disabled={formDisabled}
              />
            </div>
          )}
          <div className="sticky-action">
            <button type="submit" className="button button--primary button--block" disabled={formDisabled}>
              {t('operator.discount.reserve')}
            </button>
            {!online && (
              <p className="hint" data-testid="discount-disabled-hint">
                {t('operator.offline.needsConnection')}
              </p>
            )}
          </div>
        </form>
      )}
      {phase.kind === 'hold' && <HoldView hold={phase.hold} online={online} onDone={onDone} setFeedback={setFeedback} />}
      {phase.kind === 'done' ? <ResultCard feedback={feedback} testId="discount" /> : <FeedbackRegion feedback={feedback} testId="discount" />}
      {phase.kind === 'done' && (
        <button ref={newRef} type="button" className="button button--block" onClick={startOver} data-testid="discount-startover">
          {t('operator.discount.startOver')}
        </button>
      )}
      {phase.kind === 'hold' && <ExpiredStartOver hold={phase.hold} onStartOver={startOver} />}
    </div>
  );
}

/** After the Hold expired locally, offer a fresh start (the server releases the points). */
function ExpiredStartOver({ hold, onStartOver }: { hold: PointDiscount; onStartOver: () => void }) {
  const t = useT();
  const now = useNow(1000);
  if (Date.parse(hold.expiresAt) > now) return null;
  return (
    <button type="button" className="button" onClick={onStartOver}>
      {t('operator.discount.startOver')}
    </button>
  );
}
